import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useMount } from 'ahooks';
import { ReactNode } from 'react';

const LEGACY_QUERY_CACHE_KEY = 'reactQuery';
const LEGACY_QUERY_CACHE_MIGRATION_KEY =
  'agently:mobile:legacy-query-cache-removed:v1';

const isDevelopment =
  import.meta.env.DEV || import.meta.env.VITE_ENVIRONMENT === 'development';
const staleTime = isDevelopment ? 0 : 60 * 1000;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 60 * 24,
      staleTime,
      refetchOnWindowFocus: false,
      retry: 0,
    },
  },
});

const removeLegacyQueryCache = async () => {
  if (localStorage.getItem(LEGACY_QUERY_CACHE_MIGRATION_KEY) === 'true') {
    return;
  }

  const { del } = await import('idb-keyval');
  await del(LEGACY_QUERY_CACHE_KEY);
  localStorage.setItem(LEGACY_QUERY_CACHE_MIGRATION_KEY, 'true');
};

const scheduleLegacyQueryCacheRemoval = () => {
  const remove = () => {
    void removeLegacyQueryCache().catch((error) => {
      console.warn('Failed to remove legacy React Query cache:', error);
    });
  };

  if (typeof requestIdleCallback === 'function') {
    const callbackId = requestIdleCallback(remove, { timeout: 5000 });
    return () => cancelIdleCallback(callbackId);
  }

  const timeoutId = window.setTimeout(remove, 5000);
  return () => window.clearTimeout(timeoutId);
};

const QueryProvider = ({ children }: { children: ReactNode }) => {
  useMount(scheduleLegacyQueryCacheRemoval);

  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  );
};

const clearQueryCache = async () => {
  queryClient.clear();
};

export { QueryProvider, clearQueryCache, queryClient };
